/**
 * Needs Your Attention Card Component
 *
 * Displays urgent notifications that require user action:
 * - New referrals received
 * - Availability requests
 * - Partner connection requests
 *
 * Features:
 * - Shows max 3 items
 * - "View All" button opens modal with all notifications
 * - Swipe to clear on mobile
 * - Auto-clears when viewed/acted upon
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell, Send, Users, Calendar, CheckCircle, ChevronRight, RefreshCw, X, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDistanceToNow } from 'date-fns';
import ReferralDetailModal from './ReferralDetailModal';
import AvailabilityRequestResponseModal from './AvailabilityRequestResponseModal';
import ResendReferralModal from './ResendReferralModal';

interface NotificationItem {
  id: string;
  type:
    | 'new_referral'
    | 'referral_declined'
    | 'availability_request'
    | 'availability_request_reopened'
    | 'partner_available'
    | 'partner_request';
  title: string;
  message: string;
  createdAt: string;
  urgencyLevel?: string | null;
  metadata?: {
    referralId?: string;
    requestId?: string;
    userId?: string;
  };
}

const isUrgentLevel = (level?: string | null): boolean =>
  typeof level === 'string' && level.toLowerCase() === 'urgent';

interface NeedsAttentionResponse {
  items: NotificationItem[];
  total: number;
}

export function NeedsYourAttentionCard() {
  const { user } = useAuth();
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showAllModal, setShowAllModal] = useState(false);
  const [swipedItems, setSwipedItems] = useState<Set<string>>(new Set());

  // Modal state for different notification types
  const [selectedReferralId, setSelectedReferralId] = useState<string | null>(null);
  const [selectedAvailabilityRequestId, setSelectedAvailabilityRequestId] = useState<string | null>(null);
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const [resendReferralId, setResendReferralId] = useState<string | null>(null);

  // Fetch referral details when modal opens
  const { data: selectedReferral, isLoading: isLoadingReferral } = useQuery({
    queryKey: [`/api/workflows/referral/${selectedReferralId}`],
    queryFn: async () => {
      console.log('[NeedsYourAttentionCard] Fetching referral:', selectedReferralId);
      const response = await api.get(`/api/workflows/referral/${selectedReferralId}`);
      console.log('[NeedsYourAttentionCard] Referral data fetched:', response);

      // The API returns { data: {...}, success: true }
      // Extract the actual referral data (already in camelCase thanks to transformation layer)
      return response.data || response;
    },
    enabled: !!selectedReferralId,
  });

  // Fetch the declined referral the user wants to resend.
  const { data: resendReferral, isLoading: isLoadingResendReferral } = useQuery({
    queryKey: [`/api/workflows/referral/${resendReferralId}`, 'resend'],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/referral/${resendReferralId}`);
      return (response as any)?.data || response;
    },
    enabled: !!resendReferralId,
  });

  // Fetch availability request details when modal opens.
  // Endpoint returns { success, data: { id, jobDescription, jobLocation, expiresAt, ... } }.
  // The response modal expects `availabilityRequestId` + `cityState` + `availabilityWindow`,
  // so reshape here rather than wiring a passthrough.
  const { data: selectedAvailabilityRequest, isLoading: isLoadingAvailabilityRequest } = useQuery({
    queryKey: ['/api/workflows/availability-request', selectedAvailabilityRequestId],
    queryFn: async () => {
      const response: any = await api.get(`/api/workflows/availability-request/${selectedAvailabilityRequestId}`);
      const req = response?.data ?? response;
      if (!req?.id) return null;

      const cityState = (() => {
        const parts = (req.jobLocation || '').split(',').map((p: string) => p.trim()).filter(Boolean);
        return parts.length >= 2 ? `${parts[parts.length - 2]}, ${parts[parts.length - 1]}` : (req.jobLocation || '');
      })();

      const availabilityWindow = (() => {
        if (!req.expiresAt) return 'No deadline';
        const diffMs = new Date(req.expiresAt).getTime() - Date.now();
        if (diffMs <= 0) return 'Expired';
        const hours = Math.round(diffMs / (1000 * 60 * 60));
        if (hours < 1) return 'Less than 1 hour';
        if (hours < 24) return hours === 1 ? '1 hour' : `${hours} hours`;
        const days = Math.round(hours / 24);
        return days === 1 ? '1 day' : `${days} days`;
      })();

      return {
        availabilityRequestId: req.id,
        jobDescription: req.jobDescription,
        cityState,
        availabilityWindow,
        jobLocation: req.jobLocation,
        expiresAt: req.expiresAt,
      };
    },
    enabled: !!selectedAvailabilityRequestId,
  });

  // Fetch notifications that need attention
  const { data, isLoading } = useQuery<NeedsAttentionResponse>({
    queryKey: ['/api/workflows/needs-attention'],
    queryFn: () => api.get<NeedsAttentionResponse>('/api/workflows/needs-attention'),
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refresh every minute
    refetchOnMount: true, // Always refetch when navigating to dashboard
  });

  // Clear notification mutation
  const clearNotificationMutation = useMutation({
    mutationFn: (notificationId: string) =>
      api.post(`/api/workflows/needs-attention/clear/${notificationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
    },
    onError: () => {
      showError('Failed to clear notification');
    },
  });

  const handleClearNotification = (id: string) => {
    setSwipedItems(prev => new Set(prev).add(id));
    setTimeout(() => {
      clearNotificationMutation.mutate(id);
    }, 300); // Wait for swipe animation
  };

  /**
   * Types whose place in this list the backend derives from the underlying entity
   * (a referral still pending, a request still awaiting *this* user's response).
   *
   * Opening one of these must NOT mark it read: doing so is what used to make an
   * unanswered availability request vanish the moment you looked at it and closed
   * without deciding. They leave the list when the work is actually done — or when
   * the user explicitly dismisses them by swiping.
   *
   * Everything else has no terminal entity state to check, so it still clears on open.
   */
  const isEntityBackedType = (type: NotificationItem['type']): boolean =>
    type === 'availability_request'
    || type === 'availability_request_reopened'
    || type === 'partner_available'
    || type === 'new_referral';

  const handleNotificationClick = (item: NotificationItem) => {
    console.log('[NeedsYourAttentionCard] Notification clicked:', item.type, item.metadata);

    // Open modal/navigate immediately
    switch (item.type) {
      case 'new_referral':
        // Open ReferralDetail Modal with referralId
        if (item.metadata?.referralId) {
          console.log('[NeedsYourAttentionCard] Setting selectedReferralId:', item.metadata.referralId);
          setSelectedReferralId(item.metadata.referralId);
          setShowAllModal(false); // Close "View All" modal if open
        } else {
          console.warn('[NeedsYourAttentionCard] No referralId in metadata:', item.metadata);
        }
        break;

      case 'referral_declined':
        // Sender's view of a decline — open the Resend dialog so they can
        // forward the same client/job to another partner.
        if (item.metadata?.referralId) {
          setResendReferralId(String(item.metadata.referralId));
          setShowAllModal(false);
        } else {
          console.warn('[NeedsYourAttentionCard] No referralId on referral_declined:', item.metadata);
        }
        break;

      case 'partner_request':
        // Navigate to messages page with connection request context
        if (item.metadata?.requestId) {
          setShowAllModal(false); // Close "View All" modal if open
          navigate('/messages', {
            state: {
              connectionRequestId: item.metadata.requestId,
              userId: item.metadata.userId
            }
          });
        }
        break;

      case 'availability_request':
        // Open availability request response modal
        if (item.metadata?.requestId) {
          setSelectedAvailabilityRequestId(item.metadata.requestId);
          setSelectedNotificationId(item.id); // Let the modal clear it once a response is actually recorded
          setShowAllModal(false); // Close "View All" modal if open
        }
        break;

      case 'partner_available':
        // Sender's view: navigate to /referrals so they can convert the
        // request to a referral via the AvailabilityRequestsPanel.
        setShowAllModal(false);
        navigate('/referrals');
        break;

      case 'availability_request_reopened':
        // Recipient's view of a sender reopening a request that timed out.
        // Land them on /referrals with the Availability Requests panel
        // pre-expanded so they can act on it.
        setShowAllModal(false);
        navigate('/referrals?section=availability-requests');
        break;
    }

    // Only clear on open for types the backend can't re-derive. Entity-backed types
    // stay put until the work is done (or the user swipes them away) — opening
    // something is not the same as handling it.
    if (!isEntityBackedType(item.type)) {
      clearNotificationMutation.mutate(item.id);
    }
  };

  const getIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'new_referral':
        return <Send className="w-4 h-4 text-blue-600" />;
      case 'referral_declined':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'availability_request':
        return <Calendar className="w-4 h-4 text-orange-600" />;
      case 'availability_request_reopened':
        return <RefreshCw className="w-4 h-4 text-amber-600" />;
      case 'partner_available':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'partner_request':
        return <Users className="w-4 h-4 text-green-600" />;
      default:
        return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  const getIconBgColor = (type: NotificationItem['type']) => {
    switch (type) {
      case 'new_referral':
        return 'bg-blue-100';
      case 'referral_declined':
        return 'bg-red-100';
      case 'availability_request':
        return 'bg-orange-100';
      case 'availability_request_reopened':
        return 'bg-amber-100';
      case 'partner_available':
        return 'bg-emerald-100';
      case 'partner_request':
        return 'bg-green-100';
      default:
        return 'bg-gray-100';
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const displayItems = items.slice(0, 3);
  const hasMore = total > 3;

  return (
    <>
      <Card className="shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 bg-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-gray-700" />
              <CardTitle className="text-lg font-semibold text-gray-800">
                Needs Your Attention
              </CardTitle>
            </div>
            {total > 0 && (
              <Badge className="bg-red-500 text-white border-0 h-6 min-w-[1.5rem] flex items-center justify-center rounded-full px-2">
                {total}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? (
            // Loading skeletons
            <>
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </>
          ) : total === 0 ? (
            // Empty state message
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                You're All Caught Up!
              </h3>
              <p className="text-sm text-gray-500">
                No notifications requiring your attention
              </p>
            </div>
          ) : (
            <>
              {displayItems.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  getIcon={getIcon}
                  getIconBgColor={getIconBgColor}
                  onClear={handleClearNotification}
                  onClick={handleNotificationClick}
                  isSwiped={swipedItems.has(item.id)}
                />
              ))}

              {hasMore && (
                <Button
                  variant="ghost"
                  className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex items-center justify-center gap-1 mt-2"
                  onClick={() => setShowAllModal(true)}
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* View All Modal */}
      <Dialog open={showAllModal} onOpenChange={setShowAllModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              All Notifications ({total})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {items.map((item) => (
              <NotificationItem
                key={item.id}
                item={item}
                getIcon={getIcon}
                getIconBgColor={getIconBgColor}
                onClear={handleClearNotification}
                onClick={handleNotificationClick}
                isSwiped={swipedItems.has(item.id)}
                inModal
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Detail Modal */}
      {selectedReferralId && (
        <>
          {isLoadingReferral ? (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6">
                <p className="text-gray-600">Loading referral details...</p>
              </div>
            </div>
          ) : selectedReferral ? (
            <ReferralDetailModal
              referral={selectedReferral}
              isOpen={!!selectedReferralId}
              onClose={() => setSelectedReferralId(null)}
            />
          ) : null}
        </>
      )}

      {/* Resend Referral Modal — opened from a referral_declined notification */}
      {resendReferralId && (
        <>
          {isLoadingResendReferral ? (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6">
                <p className="text-gray-600">Loading referral details...</p>
              </div>
            </div>
          ) : resendReferral ? (
            <ResendReferralModal
              referral={resendReferral}
              isOpen={!!resendReferralId}
              onClose={() => setResendReferralId(null)}
            />
          ) : null}
        </>
      )}

      {/* Availability Request Response Modal */}
      {selectedAvailabilityRequestId && (
        <>
          {isLoadingAvailabilityRequest ? (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6">
                <p className="text-gray-600">Loading availability request details...</p>
              </div>
            </div>
          ) : selectedAvailabilityRequest ? (
            <AvailabilityRequestResponseModal
              isOpen={!!selectedAvailabilityRequestId}
              onClose={() => {
                setSelectedAvailabilityRequestId(null);
                setSelectedNotificationId(null);
              }}
              requestData={selectedAvailabilityRequest}
              notificationId={selectedNotificationId ?? undefined}
            />
          ) : null}
        </>
      )}
    </>
  );
}

function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-100">
      <Skeleton className="w-8 h-8 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}

interface NotificationItemProps {
  item: NotificationItem;
  getIcon: (type: NotificationItem['type']) => React.ReactNode;
  getIconBgColor: (type: NotificationItem['type']) => string;
  onClear: (id: string) => void;
  onClick: (item: NotificationItem) => void;
  isSwiped: boolean;
  inModal?: boolean;
}

function NotificationItem({
  item,
  getIcon,
  getIconBgColor,
  onClear,
  onClick,
  isSwiped,
  inModal = false,
}: NotificationItemProps) {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const currentTouch = e.touches[0].clientX;
    const offset = currentTouch - touchStart;
    // Only allow left swipe (negative offset)
    if (offset < 0) {
      setTouchOffset(Math.max(offset, -100));
    }
  };

  const handleTouchEnd = () => {
    if (touchOffset < -50) {
      // Swiped far enough to clear
      onClear(item.id);
    }
    setTouchOffset(0);
    setTouchStart(null);
  };

  const timeAgo = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });

  return (
    <div
      className={`relative overflow-hidden transition-all duration-300 ${
        isSwiped ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateX(${touchOffset}px)`,
        transition: touchOffset === 0 ? 'transform 0.3s ease-out' : 'none',
      }}
    >
      {/* Delete background (revealed on swipe) */}
      <div className="absolute inset-0 bg-red-500 flex items-center justify-end px-4 rounded-lg">
        <X className="w-5 h-5 text-white" />
      </div>

      {/* Main content */}
      <div
        className={`relative flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer group ${
          isUrgentLevel(item.urgencyLevel)
            ? 'bg-red-50 border-red-300 hover:border-red-400 hover:bg-red-100'
            : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
        }`}
        onClick={() => onClick(item)}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getIconBgColor(item.type)} flex-shrink-0`}>
          {getIcon(item.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p
              className={`text-sm font-medium leading-tight ${
                isUrgentLevel(item.urgencyLevel) ? 'text-red-900' : 'text-gray-800'
              }`}
            >
              {item.title}
            </p>
            {isUrgentLevel(item.urgencyLevel) && (
              <span
                className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                aria-label="Urgent referral"
              >
                Urgent
              </span>
            )}
          </div>
          <p
            className={`text-xs mt-0.5 line-clamp-1 ${
              isUrgentLevel(item.urgencyLevel) ? 'text-red-800' : 'text-gray-500'
            }`}
          >
            {item.message}
          </p>
          <p
            className={`text-xs mt-1 ${
              isUrgentLevel(item.urgencyLevel) ? 'text-red-700' : 'text-gray-400'
            }`}
          >
            {timeAgo}
          </p>
        </div>
        {inModal && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear(item.id);
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );
}
